import React from 'react';
import styles from './Button.module.css';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'warning';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon = null,
  iconPosition = 'left',
  fullWidth = false,
  onClick,
  type = 'button',
  className = '',
  ...props
}) => {
  const stylesRecord = styles as Record<string, string>;
  const buttonClasses = [
    stylesRecord['button'],
    stylesRecord[`button--${variant}`],
    stylesRecord[`button--${size}`],
    fullWidth && stylesRecord['button--fullWidth'],
    loading && stylesRecord['button--loading'],
    disabled && stylesRecord['button--disabled'],
    className
  ].filter(Boolean).join(' ');

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (!disabled && !loading && onClick) {
      onClick(e);
    }
  };

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={handleClick}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <span className={(styles as Record<string, string>)['spinner'] || ''}>
          <svg className={(styles as Record<string, string>)['spinnerIcon'] || ''} viewBox="0 0 24 24">
            <circle
              className={(styles as Record<string, string>)['spinnerCircle'] || ''}
              cx="12"
              cy="12"
              r="10"
              fill="none"
              strokeWidth="2"
            />
          </svg>
        </span>
      )}

      {!loading && icon && iconPosition === 'left' && (
        <span className={(styles as Record<string, string>)['icon'] || ''}>{icon}</span>
      )}

      <span className={(styles as Record<string, string>)['content'] || ''}>{children}</span>

      {!loading && icon && iconPosition === 'right' && (
        <span className={(styles as Record<string, string>)['icon'] || ''}>{icon}</span>
      )}
    </button>
  );
};

export default Button;

